import { ISiteLocations, IOrder, ILocationOrder, IProduct, IFlattenedProductRow, IOrderIntV2 } from '../state/orders'
import * as Payload from './payload'
import { formatDate } from '@telstra-connect/utils/data/date-helpers';
import sortBy from 'lodash-es/sortBy';

export interface IOrderPayload {
    cidn: string;
    number: string;
    name: string;
    location?: string;
    isMultiSiteOrder: boolean;
    products: IProduct[];
    siteName: string;
    siteLocations: ISiteLocations[];
    sitesComplete: string;
    progressStage: string;
    status: Payload.PayloadOrderStatus;
    estimatedDelivery: string;
    requiredDelivery?: string;
    orderClosedDate?: string;
    singleSiteOrderSiteId?: string;
    ncp?: string;
    customerName?: string;
    yourReference?: string;
    projectManager?: string;
    submittedToSDDate?: string
}

export interface IPaginationPayload {
    cursor: string,
    currentPageOffset?: number,
    nextPageOffset?: number
}

export interface IOrdersResponsePayload {
    orders: IOrderPayload[];
    numberOfActive: number;
    numberOfClosed: number;
    isPartialResponse: boolean;
    pagination: IPaginationPayload;
}

export const mapOrder = (orders: IOrder[]): IOrder[] => {
    return orders.map((order) => {
        return {
            cidn: order.cidn,
            number: order.number,
            ncp: order.ncp,
            name: order.name,
            isMultiSiteOrder: order.isMultiSiteOrder,
            sitesComplete: order.sitesComplete,
            siteLocation: order.siteLocation,
            siteName: order.siteName,
            filterLocation: order.filterLocation,
            estimatedDelivery: order.estimatedDelivery,
            requiredDelivery: order.requiredDelivery,
            closedDate: order.closedDate,
            customerName: order.customerName,
            status: order.status,
            yourReference: order.yourReference,
            projectManager: order.projectManager,
            submittedToSDDate: order.submittedToSDDate,
        }
    });
}

export const mapLocationOrder = (orders: IOrder[]): ILocationOrder[] => {
    let orderByLocation = [];
    let counter = 1;
    orders.forEach((order) => {
        const productsList = [];
        order.products.forEach((item) => {
            const obj = {
                name: item.name,
                serviceId: item.fnn,
                productType: item.productType,
                billing: item.billing,
                productStatus: item.productStatus,
                productEstimatedDelivery: item.productEstimatedDelivery,
                productRequiredDelivery: item.productRequiredDelivery,
                upcomingAppointment: item.upcomingAppointment,
                productClosedDate: item.productClosedDate,
                deliveryAddress: item.deliveryAddress,
                siteName: item.siteName,
                siteAddress: item.location ? getFormatedSiteAddress(item.location, order.cidn) : '-',
                number: order.number,
                ncp: order.ncp,
                yourReference: order.yourReference,
                customerName: order.customerName,
                cidn: order.cidn,
                offer: item.offer,
                location: item.location ? locationCheck(item.location, order.cidn) : '-',
                id: counter,
                submittedToSDDate: order.submittedToSDDate,
            }
            counter++;
            productsList.push(obj);
        });
        orderByLocation = orderByLocation.concat(productsList);
    });
    return sortBy(orderByLocation, ['location']);
}

export const formatOrder = (payload: IOrderPayload): IOrder => ({
    cidn: payload.cidn,
    number: payload.number,
    name: payload.name,

    ...payload.ncp !== undefined && {
        ncp: payload.ncp,
    },
    isMultiSiteOrder: payload.isMultiSiteOrder,
    sitesComplete: payload.sitesComplete,
    siteLocation: payload.siteLocations,
    products: getProducts(payload.products, payload.cidn),
    // siteName: payload.siteName,
    filterLocation: createFilterLocations(payload.siteLocations),
    progressStage: payload.progressStage,
    requiredDelivery: payload.requiredDelivery,
    customerName: payload.customerName || '-',
    status: Payload.mapStatus(payload.status),
    ...payload.singleSiteOrderSiteId && {
        singleSiteOrderSiteId: payload.singleSiteOrderSiteId
    },
    yourReference: payload.yourReference,
    submittedToSDDate: payload.submittedToSDDate,
    projectManager: payload.projectManager,
    ...Payload.mapDates(payload, Payload.mapStatus(payload.status))
});

export const formatOrderProducts = (
    orders: IOrderIntV2[]
): IFlattenedProductRow[] => {
    const rows: IFlattenedProductRow [] = [];
    orders.forEach((order) => {
        rows.push({
            orderNumber: order.orderNumber,
            cidn: order.cidn,
            customerName: order.customerName,
            projectManager: order.projectManager,
            yourReference: order.yourReference,
            orderStatus: order.orderStatus,
            submittedToSDDate: order.submittedToSDDate,
            estimatedDelivery: order.estimatedDelivery,
            requiredDelivery: order.requiredDelivery,
            ncp: order.ncp,
            name: order.name,
            productStatus: order.productStatus,
            productType: order.productType,
            location: order.location,
            productEstimatedDelivery: order.productEstimatedDelivery,
            productRequiredDelivery: order.productRequiredDelivery,
            upcomingAppointment: order.upcomingAppointment,
            productClosedDate: order.productClosedDate
        })});

    return rows;
};

export const flattenOrderIntProducts = (
    orders: IOrder[]
): IOrderIntV2[] => {
    const rows: IOrderIntV2[] = [];
    orders.forEach((order) => {
        order.products.forEach((product) => {
            rows.push({
                ...product,
                orderNumber: order.number,
                cidn: order.cidn,
                customerName: order.customerName,
                projectManager: order.projectManager,
                yourReference: order.yourReference,
                orderStatus: order.status,
                submittedToSDDate: order.submittedToSDDate,
                estimatedDelivery: order.estimatedDelivery,
                requiredDelivery: order.requiredDelivery,
                ncp: order.ncp,
                isMultiSiteOrder: false,
                sitesComplete: '',
                siteLocation: [],
                filterLocation: order.filterLocation
            });
        });
    });

    return rows;
};

function createFilterLocations(locations) {
    const filterLocations = [];
    if (locations && locations.length) {
        locations.forEach((l) => {
            if (l.suburb && l.state) {
                filterLocations.push({
                    value: l.suburb + ', ' + l.state,
                    label: l.suburb + ', ' + l.state
                });
            } else if (l.suburb) {
                filterLocations.push({
                    value: l.suburb,
                    label: l.suburb
                });
            }

            if (l.state) {
                filterLocations.push({
                    value: l.state,
                    label: l.state
                });
            }
        });
    }

    return filterLocations;
}

function getProducts(products, cidn) {
    const productList = [];
    products.forEach((item) => {
        const obj = {
            name: item.name || '-',
            productType: item.productType || '-',
            fnn: item.fnn || '-',
            productStatus: item.status || '-',
            offer: item.offer || '-',
            deliveryAddress: item.deliveryAddress || '-',
            billing: item.completedDate ? formatDate(item.billingDate) : '-',
            location: item.location,
            siteName: item.siteName || '-',
            ...Payload.mapDates(item, Payload.mapStatus(item.status), 'products'),
        }
        productList.push(obj);
    });
    return productList;
}

function locationCheck(location: string, cidn: string) {
    if (isInternationalCidn(cidn)) {
        const locValue = location.split('---');
        location = (locValue.length === 2) ?
            getFormatedLocation(locValue[0], cidn) +
            (checkInitialAddress(locValue[1]) && (' - ' + getFormatedLocation(locValue[1], cidn))
            || '')

            : getFormatedLocation(locValue[0], cidn);
        return location;
    } else {
        return getFormatedLocation(location, cidn);
    }
}

function getFormatedLocation(location: string, cidn: string) {
    const isInternational = isInternationalCidn(cidn);
    const locValue = isInternational ?
    location.split(',').reverse() :
    location.split(',');
    const locLen = (isInternational) ? 5 : locValue.length;
    let street = '';
    let pincode = '';
    let state = '';
    let suburb = '';
    let country = '';
    if (locLen >= 4) {
        country = locValue[locLen - 1] !== undefined ? locValue[locLen - 1] : '';
        pincode = locValue[locLen - 2] !== undefined ? locValue[locLen - 2] : '';
        state = locValue[locLen - 3] !== undefined ? locValue[locLen - 3] : '';
        suburb = locValue[locLen - 4] !== undefined ? locValue[locLen - 4] : '';
        locValue.forEach((element, index) => {
            if (index < locLen - 4 && element !== undefined) {
                index === 0 ? street = element : street = street + ', ' + element;
            }
        });
        if (isInternational) {

         return (suburb.trim())
                + (suburb.trim() ? ', ' + state : state)
                + ((state.trim() && pincode.trim()) ? ', ' + pincode : pincode)
                + ((pincode.trim() && country.trim()) ? ', ' + country : country);

        } else {
            return suburb.trim() + ', ' + state + ', ' + pincode + ', ' + street;
        }
    }
    return location;
}

function getFormatedSiteAddress(location: string, cidn: string) {
    if (isInternationalCidn(cidn)) {
        const locationArr = location.split('---');
        let locationValue = '';
        locationValue = formatIntSiteLoc(locationArr[0]);

        if (locationArr.length > 1) {
            locationValue = locationValue + ' - ' + formatIntSiteLoc(locationArr[1]);
        }
        locationValue = locationValue.trim().replace(/^,|^ ,|,$| ,$|(, )+/g, '$1');
        locationValue = locationValue.trim().replace(/^,|,$|( ,)/g, '');
        return locationValue;
    }
    return location;
}

function isInternationalCidn(cidn: string) {
    return (cidn && (cidn.length >= 5 && cidn.length <= 8)) ? true : false;
}

function checkInitialAddress(locString: string) {
    const stringArray = locString.split(',').reverse();
    let isValue = false;
    const loopLength = (stringArray.length && ((stringArray.length >= 4 && 5)
    ||  stringArray.length)) || 0;

    for (let i = 1 ; i < loopLength ; i++) {
        if (stringArray[i].trim().length > 0) {
                isValue = true;
                break;
        }
    }
    return isValue;
}

function formatIntSiteLoc(location: string) {
    let locList = location.split(',');
    locList = locList.filter((zone) => {
          if (zone !== ' ' && zone !== '') {
              return zone.trim();
          }
    })
    location = locList.join();
    return location
}
